<template>
    <fieldset :class="formFieldClasses">
        <SpacingClassWrapper :padding-x="0">
            <!-- Form field Top Label -->
            <SpacingClassWrapper :padding-bottom="2">
                <Typography
                    :variant="typographyVariants.label"
                    :color="fieldData.validity.invalid ? typographyColors.error : typographyColors.dark"
                    :weight="typographyWeights.regular"
                    :for="fieldName"
                    :class="formFieldLabelClasses"
                >
                    {{ t(`form.fields[${fieldName}].label`) }}
                </Typography>
            </SpacingClassWrapper>
            <!-- Form Element -->
            <SpacingClassWrapper :padding-x="2">
                <FormElement 
                    :ref="fieldName"
                    v-bind="fieldAttr"

                    :name="fieldName"
                    :value="fieldValue"
                    :variant="fieldData.variant"
                    :invalid="fieldData.validity.invalid"

                    @input="onFormFieldInput"
                    @change="(event) => { fieldData.options && onFormFieldInput(event)}"
                    @invalid.prevent="checkValidity"
                >
                    <option
                        v-for="(option, key) in fieldData.options"
                        :key="key"
                        :value="option"
                    >
                        {{ t(`form.fields.${fieldName}.optionLabels.${option}`) || option }}
                    </option>
                </FormElement>
            </SpacingClassWrapper>
            <!-- Select Form element custom arrow -->
            <Chevron
                v-if="fieldData.options"
                :class="formFieldChevronClasses"
                :width="9"
                :height="5"
                :color="fieldData.validity.invalid ? chevronColors.error : chevronColors.primary"
                :direction="chevronDirections.up"
            />
            <!-- Form element error msg -->
            <SpacingClassWrapper
                :margin-top="$mq | mq({ sm: 0, md: 1 })"
                :margin-right="2"
            >
                <FlexClassWrapper>
                    <Typography
                        v-if="fieldData.validity.invalid"
                        :variant="typographyVariants.caption"
                        :color="typographyColors.error"
                        :class="formFieldErrorClasses"
                    >
                        {{ formFieldErrorText }}
                    </Typography>
                </FlexClassWrapper>
            </SpacingClassWrapper>
        </SpacingClassWrapper>
    </fieldset>
</template>

<script>
// Components
import Typography from '../../components/Typography';
import Chevron from '../../components/Chevron';
import FormElement from '../../components/FormElement';

// Class Wrappers
import SpacingClassWrapper from '../../classWrappers/SpacingClassWrapper';
import FlexClassWrapper from '../../classWrappers/FlexClassWrapper';

// Types
import { 
    typographyVariants, 
    typographyColors, 
    typographyWeights 
} from '../../types/Typography.types';
import { chevronColors, chevronDirections } from '../../types/Chevron.types'

// Mixins
import styles from '../../mixins/styles';
import translations from '../../mixins/translations';

export default {
    name: 'FormFieldContainer',
    components: {
        // Components
        Typography,
        FormElement,
        Chevron,
        // Class Wrappers
        SpacingClassWrapper,
        FlexClassWrapper
    },
    mixins: [styles, translations],
    props: {
        fieldName: {
            type: String,
            default: ''
        },
        fieldAttr: {
            type: Object,
            default: () => ({})
        },
        fieldData: {
            type: Object,
            default: () => ({})
        },
        fieldValue: {
            type: [String, Number],
            default: '',
        }
    },
    data: function () {
        return {
            // Types
            typographyVariants, 
            typographyColors, 
            typographyWeights,
            chevronColors,
            chevronDirections
        }
    },
    computed: {
        isMobile: function() { 
            return this.$mq === 'sm' || this.$mq === 'md';
        },
        isTablet: function() {
            return this.$mq === 'xl';
        },
        formFieldClasses: function () {
            return [
                this.cssClasses.pcc__formField,
                this.cssClasses[`pcc__formField_${this.isMobile ? 'mobile' : this.isTablet ? 'tablet' : 'desktop'}`],
            ];
        },
        formFieldLabelClasses: function () {
            return [
                this.cssClasses.pcc__formField_label
            ];
        },
        formFieldChevronClasses: function () {
            return [
                this.cssClasses.pcc__formField_SelectChevron
            ];
        },
        formFieldErrorClasses: function() {
            return this.cssClasses[`pcc__formField_${this.isMobile ? 'error-mobile' : 'error-desktop'}`]
        },
        formFieldErrorText: function () {
            const { invalid, ...restValidity } = this.fieldData.validity;
            const restValidityEntries = Object
                .entries(restValidity)
                .filter(([_, validityValue]) => Boolean(validityValue));

            if (restValidityEntries.length) {
                const [firstError] = restValidityEntries;
                const [validityName] = firstError;
                return this.t(`form.fields[${this.fieldName}].errorTexts[${validityName}]`);
            }
            return this.t(`form.fields.requiredError`);
        },
    },
    methods: {
        onFormFieldInput: function (event) {
            this.valueChange(event.target.value);
            this.checkValidity();
        },
        getFieldValidity: function () {
            const fieldValidity = this.$refs[this.fieldName].$el.validity;
            fieldValidity.invalid = !fieldValidity.valid;
            return fieldValidity;
        },
        valueChange: function (newValue) {
            this.$emit(
                'value-change',
                {
                    fieldName: this.fieldName,
                    newValue
                }
            );
        },
        checkValidity: function () {
            this.$emit(
                'check-validity',
                {
                    fieldName: this.fieldName,
                    validity: this.getFieldValidity()
                }
            )
        },
    }
};
</script>
